import OfferSchedule from '../OfferSchedule';
import { scheduleInstant } from '../../schedule';
import { formatBudgetEstimate, useBudgetEstimate } from '../../budgetEstimate';
import useOfferImagePreview, { OfferImagePreviewStatus } from '../../useOfferImagePreview';
import React from 'react'
import { get } from 'lodash'
import moment from 'moment'
import { useSelector } from 'react-redux'
import {
  Box,
  Alert,
  Button,
  Card,
  CardHeader,
  Grid,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
} from '@mui/material'
import { Edit } from '@mui/icons-material'

import { OfferCard } from 'components'
import { period } from 'utils/constants'
import OfferListPreview from '../OfferListPreview'
import { getBalanceDisplay } from 'utils/balanceColor';
import { hasMinimumLaunchBalance } from 'utils/businessBalance';
import OfferSaveActions from '../OfferSaveActions'
import WizardBackButton from '../WizardBackButton'
import {
  configureOfferPreviewCardProps,
  getClaimLimitDisplay,
} from '../OfferWizard/utils'
import { getOfferFulfillmentLabel } from 'modules/Business/modules/Offers/fulfillment'

const OfferReview = ({
  back,
  onComplete,
  values,
  preserveLegacyImage = false,
  resetToStart,
  onSave,
  onUpdate,
  saveButtonLabel,
  isEditingActive,
  serverReviewReady = true,
}) => {
  const business = useSelector(state => state.business) // Get business state
  const estimate = useBudgetEstimate(onUpdate ? null : business.businessId, values);
  const businessLogo = useSelector(state => get(state, 'business.companyInfo.logoUrl'))
  const imagePreview = useOfferImagePreview(business.businessId ?? business.companyInfo?.businessId, values.details?.offerImage, !preserveLegacyImage);

  const handleFormSubmit = (data) => {
    onComplete(data)
  }

  if (!values.description || !values.budget || !values.details || !values.audience) {
    return null
  }

  // Same logic as Offer Details: show call/location icons when enabled in audience
  const showPhone = values?.audience?.local?.some(loc => loc.showPhone === true) || false
  const showAddress = values?.audience?.local?.some(loc => loc.showAddress === true) || false
  const phoneForCard = (() => {
    const withPhone = values?.audience?.local?.find(loc => loc.showPhone === true)
    return withPhone?.phoneNumber ?? withPhone?.phone ?? null
  })()

  const isFundedForLaunch = hasMinimumLaunchBalance(business.accountBalance, business);
  const reviewBlockMessage = !serverReviewReady
    ? 'This draft must pass review before it can be launched.'
    : '';
  const submitButtonLabel = (() => {
    if (!isFundedForLaunch) return 'Next';
    if (isEditingActive && moment(values.budget.endDate).isBefore(moment())) {
      return 'Update & expire';
    }
    if (moment(values.budget.startDate).isAfter(moment())) {
      return isEditingActive ? 'Update & schedule' : 'Schedule';
    }
    return isEditingActive ? 'Update offer' : 'Launch';
  })();

  return (<>
    <Box mb={4}>
      <Typography variant="h6" component="h2">
        Review your offer
      </Typography>
    </Box>
    {/* Rest of the offer review content */}
    <Grid container spacing={3}>
      <Grid item lg={6} sm={12}>
        <Card>
          <CardHeader
            subheader="Summary"
            action={<IconButton aria-label="Edit offer" onClick={resetToStart} size="large">
              <Edit />
            </IconButton>}
          />
          <Table>
            <TableBody>
              <TableRow>
                <TableCell>
                  Title
                </TableCell>
                <TableCell align="right">
                  {values.description.title}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Fulfillment
                </TableCell>
                <TableCell align="right">
                  {getOfferFulfillmentLabel(values.description.fulfillment) || 'Not selected'}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Categories
                </TableCell>
                <TableCell align="right">
                  {values.description.category.map(c => c.name).join(', ')}
                  {/* {values.description.category.map(c => c.categoryName).join(', ')} */}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>
                  Keywords
                </TableCell>
                <TableCell align="right">
                  {
                    Array.isArray(values.description.keywords) &&
                    values.description.keywords.join(', ')
                  }
                </TableCell>
              </TableRow>
              <OfferSchedule
                startAt={scheduleInstant(values.budget.startDate)}
                endAt={scheduleInstant(values.budget.endDate)}
                variant="table"
                showEmptyFields
              />
              <TableRow>
                <TableCell>
                  Budget
                </TableCell>
                <TableCell align="right">
                  ${values.budget.maxAmount} {Object.keys(period).find(p => period[p] === values.budget.amountPeriod)}
                </TableCell>
              </TableRow>
              {estimate && (
                <TableRow>
                  <TableCell>Budget capacity</TableCell>
                  <TableCell align="right">{formatBudgetEstimate(estimate)}</TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell>
                  Claims
                </TableCell>
                <TableCell align="right">
                  {getClaimLimitDisplay(values.budget)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Card>
      </Grid>
      <Grid item sm={12} lg={6} xs={12}>
        <Box position="sticky" top={0}>
          <Box mx="auto" maxWidth={450}>
            <Box textAlign="right">
              <OfferListPreview offer={{
                ...values.details,
                ...configureOfferPreviewCardProps,
                endDate: scheduleInstant(get(values, 'budget.endDate')),
                imageUrl: imagePreview.imageUrl,
                businessImageUrl: businessLogo,
                claimsRemaining: values.budget.maxClick,
                unlimitedClaims: values.budget.unlimitedClaims === true,
                showPhone,
                showAddress,
                phone: phoneForCard,
                offerLocation: values.audience,
              }} />
            </Box>
            <OfferImagePreviewStatus {...imagePreview} />
            <OfferCard
              {...values.details}
              {...configureOfferPreviewCardProps}
              isPreview
              imageUrl={imagePreview.imageUrl}
              businessImageUrl={businessLogo}
              endDate={scheduleInstant(get(values, 'budget.endDate'))}
              claimsRemaining={values.budget.maxClick}
              unlimitedClaims={values.budget.unlimitedClaims === true}
              showPhone={showPhone}
              showAddress={showAddress}
              phone={phoneForCard}
              offerLocation={values.audience}
            />
          </Box>
        </Box>
      </Grid>
    </Grid>
    {reviewBlockMessage && (
      <Alert severity="error" sx={{ mt: 2 }}>
        {reviewBlockMessage}
      </Alert>
    )}
    <Box mt={2} mb={1} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
      
      <OfferSaveActions
        onUpdate={!isFundedForLaunch ? onUpdate : undefined}
        onSave={() => onSave({})}
        saveButtonLabel={saveButtonLabel}
      />

      
      <Box display="flex" alignItems="center">
        {(() => {
    try {
        const balanceDisplay = getBalanceDisplay(
          business.accountBalance,
          'Account balance:',
          business
        );
      return (
        <Typography variant="body2" color={balanceDisplay.color}>
          {balanceDisplay.text}
        </Typography>
      );
    } catch (error) {
      // Handle any potential errors (e.g., if accountBalance is not in the expected format)
      
      return (
        <Typography variant="body2" color="error">
          Unable to display account balance. Please check your account settings.
        </Typography>
      );
    }
        })()}
          <WizardBackButton onClick={back} />
          <Button
            color="primary"
            onClick={handleFormSubmit}
            variant="contained"
            type="submit"
            disabled={Boolean(reviewBlockMessage)}
            style={{ marginRight: '16px' }}
          >
            {submitButtonLabel}
          </Button>
          
        </Box>
    </Box>
  </>);
}

export default OfferReview
